/**
 * RegimeTimelineChart — score evolution over time with regime thresholds
 */
import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  ComposedChart, Line, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine,
} from 'recharts';

const RegimeTimelineChart = ({ data, indexCode = 'VN30' }) => {
  const { t } = useTranslation();

  if (!data || data.length === 0) {
    return (
      <Box sx={{ py: 3, textAlign: 'center' }}>
        <Typography sx={{ fontSize: '0.78rem', color: '#94a3b8' }}>{t('highVolRegime.noData', 'No data')}</Typography>
      </Box>
    );
  }

  const chartData = data.map(item => ({
    date: new Date(item.trade_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    score: item.score || 0,
    regime: item.regime,
    triggers: item.trigger_count || 0,
  }));

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    const clr = d.regime === 'HIGH_VOL' ? '#ef4444' : d.regime === 'CAUTION' ? '#f59e0b' : '#10b981';
    return (
      <Box sx={{ bgcolor: '#fff', border: '1px solid #e2e8f0', borderRadius: 1.5, p: 1.2, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#1e293b', mb: 0.3 }}>{d.date}</Typography>
        <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: clr }}>{d.regime}</Typography>
        <Typography sx={{ fontSize: '0.65rem', color: '#64748b' }}>{t('highVolRegime.score', 'Score')}: {d.score.toFixed(1)}/80 · {t('highVolRegime.triggers', 'Triggers')}: {d.triggers}/6</Typography>
      </Box>
    );
  };

  const LEGEND = [
    { label: t('highVolRegime.regimes.NORMAL.label', 'Normal'), color: '#10b981' },
    { label: t('highVolRegime.regimes.CAUTION.label', 'Caution'), color: '#f59e0b' },
    { label: t('highVolRegime.regimes.HIGH_VOL.label', 'High Vol'), color: '#ef4444' },
  ];

  return (
    <Box sx={{ borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#fff', overflow: 'hidden' }}>
      {/* Header */}
      <Box sx={{ px: 2, py: 1.2, borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#1e293b' }}>
            {indexCode} — {t('highVolRegime.charts.regimeTimeline', 'Regime Timeline')}
          </Typography>
          <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8' }}>
            {t('highVolRegime.charts.timelineDesc', 'Score evolution and regime changes')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          {LEGEND.map(l => (
            <Box key={l.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: l.color }} />
              <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8' }}>{l.label}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Chart */}
      <Box sx={{ px: 1, py: 1 }}>
        <ResponsiveContainer width="100%" height={260}>
          <ComposedChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id={`sg-${indexCode}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.2} />
                <stop offset="100%" stopColor="#3b82f6" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} />
            <YAxis domain={[0, 80]} tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} width={32} />
            <Tooltip content={<CustomTooltip />} />
            <ReferenceLine y={40} stroke="#f59e0b" strokeDasharray="4 4" label={{ value: 'CAUTION', position: 'right', fill: '#f59e0b', fontSize: 9 }} />
            <ReferenceLine y={60} stroke="#ef4444" strokeDasharray="4 4" label={{ value: 'HIGH_VOL', position: 'right', fill: '#ef4444', fontSize: 9 }} />
            <Area type="monotone" dataKey="score" fill={`url(#sg-${indexCode})`} stroke="none" />
            <Line type="monotone" dataKey="score" stroke="#3b82f6" strokeWidth={2} dot={{ r: 2.5, fill: '#3b82f6' }} activeDot={{ r: 4 }} />
          </ComposedChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

export default RegimeTimelineChart;
